import api, { invalidateMonitoringSettingsCache } from './api';
import { API_BASE_URL } from '../config';
import { getBearerTokenForApi } from '../utils/poolSiteAuth';

const LEAVE_PATH = '/central-ai-monitor/presence/leave';

function leaveRequestUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, '');
  return `${base}${LEAVE_PATH}`;
}

/**
 * 창 X·pagehide 시 비동기 axios가 취소되므로 fetch keepalive 로 leave 전송.
 */
export function sendLeaveCentralAiPresenceKeepalive(): void {
  if (typeof window === 'undefined') return;
  const token = getBearerTokenForApi();
  try {
    void fetch(leaveRequestUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: '{}',
      keepalive: true,
      credentials: 'include',
    });
  } catch {
    /* unload 중 */
  }
}

/** 통합 관제 종료 — 감시 중지 후 (가능하면) 창 닫기 */
export async function endCentralMonitoringSession(): Promise<void> {
  sendLeaveCentralAiPresenceKeepalive();
  await leaveCentralAiPresence();
}

export type CentralAiSnapshot = {
  pool_id: number;
  cctv_id: number;
  updated_at: number;
  mount_type?: string;
  detections: unknown[];
  risk_alerts: unknown[];
  frame_width?: number;
  frame_height?: number;
  openpose_ran?: boolean;
  openpose_overlay?: unknown;
  /** raw_person = 원본 YOLO 진단 */
  pipeline_mode?: string;
};

export type CentralAiStatus = {
  worker_alive: boolean;
  server_ai_enabled: boolean;
  audience_active?: boolean;
  excluded_pool_ids: number[];
  included_pool_ids?: number[];
  central_monitor_opt_in?: boolean;
  snapshot_count: number;
  inference_job_count?: number;
};

/** 서버 기준으로 central-ai 추론·presence가 의미 있는지 (감시 ON 시설 없으면 false) */
export function centralAiHasActiveMonitoringTargets(
  st: Pick<
    CentralAiStatus,
    'included_pool_ids' | 'central_monitor_opt_in' | 'inference_job_count'
  >
): boolean {
  if ((st.inference_job_count ?? 0) > 0) return true;
  const optIn = st.central_monitor_opt_in !== false;
  if (optIn) {
    return (st.included_pool_ids ?? []).length > 0;
  }
  return (st.inference_job_count ?? 0) > 0;
}

/** 관제 로그인 세션 하트비트 — 서버 AI 추론은 이 신호가 있을 때만 동작 */
export async function pingCentralAiPresence(): Promise<void> {
  await api.post('/api/central-ai-monitor/presence', {}, { silentError: true });
}

/** 통합 관제 창 닫힘·화면 이탈 시 서버 AI 추론 즉시 중지 */
export async function leaveCentralAiPresence(): Promise<void> {
  try {
    await api.post('/api/central-ai-monitor/presence/leave', {}, { silentError: true });
  } catch {
    /* 404: 백엔드 재기동 전 등 */
  }
}

export async function fetchCentralAiSnapshots(params?: {
  pool_id?: number;
  cctv_id?: number;
}): Promise<CentralAiSnapshot[]> {
  const data = await api.get<CentralAiSnapshot[]>(
    '/api/central-ai-monitor/snapshots',
    params,
    { useCache: false, silentError: true }
  );
  return Array.isArray(data) ? data : [];
}

export async function fetchCentralAiStatus(): Promise<CentralAiStatus> {
  return api.get<CentralAiStatus>(
    '/api/central-ai-monitor/status',
    undefined,
    { useCache: false, silentError: true }
  );
}

/** 통합 관제 팝업 진입 — 전 시설 AI 감시 OFF */
export async function resetCentralPoolMonitoringAllOff(): Promise<{
  excluded_pool_ids: number[];
  included_pool_ids: number[];
}> {
  const data = await api.post<{
    excluded_pool_ids: number[];
    included_pool_ids?: number[];
  }>('/api/central-ai-monitor/reset-pool-monitoring', {}, { silentError: true });
  invalidateMonitoringSettingsCache();
  return {
    excluded_pool_ids: Array.isArray(data?.excluded_pool_ids)
      ? data.excluded_pool_ids
      : [],
    included_pool_ids: Array.isArray(data?.included_pool_ids)
      ? data.included_pool_ids
      : [],
  };
}

export async function excludePoolFromCentralAi(poolId: number): Promise<{
  excluded_pool_ids: number[];
  included_pool_ids: number[];
}> {
  const data = await api.post<{
    excluded_pool_ids: number[];
    included_pool_ids?: number[];
  }>(`/api/central-ai-monitor/exclude-pool/${poolId}`);
  invalidateMonitoringSettingsCache();
  return {
    excluded_pool_ids: Array.isArray(data?.excluded_pool_ids)
      ? data.excluded_pool_ids
      : [],
    included_pool_ids: Array.isArray(data?.included_pool_ids)
      ? data.included_pool_ids
      : [],
  };
}

export async function includePoolInCentralAi(poolId: number): Promise<{
  excluded_pool_ids: number[];
  included_pool_ids: number[];
}> {
  const data = await api.delete<{
    excluded_pool_ids: number[];
    included_pool_ids?: number[];
  }>(`/api/central-ai-monitor/exclude-pool/${poolId}`);
  invalidateMonitoringSettingsCache();
  return {
    excluded_pool_ids: Array.isArray(data?.excluded_pool_ids)
      ? data.excluded_pool_ids
      : [],
    included_pool_ids: Array.isArray(data?.included_pool_ids)
      ? data.included_pool_ids
      : [],
  };
}
